import React from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AiChatCitation from './AiChatCitation';

/** Custom citation link schemes emitted by the agent, e.g. `workplan:FIN.WP.009`. */
const CITATION_HREF_RE = /^(workplan|ridd):(.+)$/i;

function classNames(...names) {
  return names.filter(Boolean).join(' ');
}

function withoutMarkdownNode(props) {
  const elementProps = { ...props };
  delete elementProps.node;
  return elementProps;
}

/**
 * Preserve the agent's custom citation schemes (which `defaultUrlTransform`
 * would otherwise strip), while keeping standard sanitization for real links.
 */
function citationUrlTransform(url, key, node) {
  if (CITATION_HREF_RE.test(url)) {
    return url;
  }
  return defaultUrlTransform(url, key, node);
}

const markdownComponents = {
  ul(props) {
    const elementProps = withoutMarkdownNode(props);
    return (
      <ul
        {...elementProps}
        className={classNames(
          'ddv-ai-chat__markdown-list',
          'ddv-ai-chat__markdown-list--unordered',
          elementProps.className,
        )}
      />
    );
  },
  ol(props) {
    const elementProps = withoutMarkdownNode(props);
    return (
      <ol
        {...elementProps}
        className={classNames(
          'ddv-ai-chat__markdown-list',
          'ddv-ai-chat__markdown-list--ordered',
          elementProps.className,
        )}
      />
    );
  },
  li(props) {
    const elementProps = withoutMarkdownNode(props);
    return (
      <li
        {...elementProps}
        className={classNames(
          'ddv-ai-chat__markdown-list-item',
          elementProps.className,
        )}
      />
    );
  },
  a(props) {
    const elementProps = withoutMarkdownNode(props);
    const match = CITATION_HREF_RE.exec(elementProps.href || '');
    if (match) {
      const type = match[1].toLowerCase();
      const itemId = match[2];
      return (
        <AiChatCitation type={type} itemId={itemId}>
          {elementProps.children}
        </AiChatCitation>
      );
    }
    return <a {...elementProps} />;
  },
  table(props) {
    const elementProps = withoutMarkdownNode(props);
    return (
      <div className="ddv-ai-chat__markdown-table-wrap">
        <table
          {...elementProps}
          className={classNames(
            'ddv-ai-chat__markdown-table',
            elementProps.className,
          )}
        />
      </div>
    );
  },
};

/**
 * Markdown rendering for assistant messages (GFM tables via remark-gfm; no raw HTML).
 */
export default function MarkdownContent({ text }) {
  if (!text) {
    return null;
  }

  return (
    <div className="ddv-ai-chat__markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
        urlTransform={citationUrlTransform}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
